import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { isClickedOutside, lockScroll, unlockScroll, mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';
import { shadeSelection, initialShadeIndex } from '@theme/eme-shade-sheet-core';

class EmeShadeSheet extends Component {
  requiredRefs = ['dialog', 'close', 'productForm', 'productLink', 'price', 'selectedName'];

  #trigger = null;
  #index = 0;
  #card = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#stopCardNavigation);
    this.addEventListener('change', this.#handleChange);
    this.refs.dialog.addEventListener('click', this.#handleBackdrop);
    this.refs.dialog.addEventListener('close', this.#handleClosed);
    this.refs.productForm.addEventListener(StandardEvents.cartLinesUpdate, this.#handleAdd);
    mediaQueryLarge.addEventListener('change', this.#handleBreakpoint);
    this.#card = this.closest('product-card');
    this.#card?.addEventListener(StandardEvents.productSelect, this.#handleCardSelect);
    this.#markTrigger();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#stopCardNavigation);
    this.removeEventListener('change', this.#handleChange);
    this.refs.dialog.removeEventListener('click', this.#handleBackdrop);
    this.refs.dialog.removeEventListener('close', this.#handleClosed);
    this.refs.productForm.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleAdd);
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpoint);
    this.#card?.removeEventListener(StandardEvents.productSelect, this.#handleCardSelect);
    this.#card = null;
    unlockScroll(this);
  }

  open(trigger = null) {
    this.#trigger = trigger;
    const checked = this.#card?.querySelector('swatches-variant-picker-component input:checked');
    this.#select(initialShadeIndex(this.#shades(), checked instanceof HTMLInputElement ? checked.value : ''));
    this.#show();
  }

  #show() {
    if (this.refs.dialog.open) return;
    lockScroll(this);
    this.refs.dialog.showModal();
    setTimeout(() => this.refs.close.focus(), 50);
  }

  close() {
    if (this.refs.dialog.open) this.refs.dialog.close();
  }

  #inputs() {
    return [...this.querySelectorAll('.eme-shade-sheet__input')].filter((input) => input instanceof HTMLInputElement);
  }

  #shades() {
    return this.#inputs().map((input) => ({
      value: input.value,
      variantId: input.dataset.variantId ?? '',
      available: input.dataset.available === 'true',
      price: input.dataset.price ?? '',
      compareAtPrice: input.dataset.compareAtPrice ?? '',
      media: input.dataset.media ?? '',
      connectedUrl: input.dataset.connectedUrl ?? '',
    }));
  }

  #select(index) {
    const selection = shadeSelection(this.#shades(), index, this.dataset.productUrl ?? '');
    if (!selection) return;
    this.#index = index;
    const input = this.#inputs()[index];
    if (input) input.checked = true;
    this.refs.selectedName.textContent = selection.name;
    this.refs.price.textContent = selection.price;
    const { compareAtPrice, media } = this.#shades()[index] ?? {};
    const { compare } = this.refs;
    if (compare instanceof HTMLElement) {
      compare.textContent = compareAtPrice ?? '';
      compare.hidden = !compareAtPrice;
    }

    const form = this.refs.productForm;
    const variantInput = form.querySelector('input[name="id"]');
    const button = form.querySelector('[ref="addToCartButton"]');
    const buttonContainer = form.querySelector('add-to-cart-component');
    if (buttonContainer instanceof HTMLElement && media) buttonContainer.dataset.productVariantMedia = media;
    if (media) this.#showThumb(media);
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = selection.variantId;
      variantInput.disabled = !selection.available;
    }
    if (button instanceof HTMLButtonElement) {
      button.disabled = !selection.available;
      const label = selection.available ? this.dataset.addLabel : this.dataset.soldOutLabel;
      const labelNode = [...button.childNodes].find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim()
      );
      if (label && labelNode) labelNode.textContent = label;
    }
    form.hidden = Boolean(selection.href);
    this.refs.productLink.hidden = !selection.href;
    if (selection.href) this.refs.productLink.href = selection.href;
  }

  #showThumb(src) {
    const { thumb } = this.refs;
    if (!(thumb instanceof HTMLElement)) return;
    let image = thumb.querySelector('img');
    if (!image) {
      image = document.createElement('img');
      image.width = 80;
      image.height = 80;
      image.alt = '';
      thumb.append(image);
    }
    if (image.getAttribute('src') === src) return;
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = src;
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.classList.contains('eme-shade-sheet__input')) return;
    this.#select(this.#inputs().indexOf(event.target));
  };

  #handleAdd = async (event) => {
    this.close();
    try {
      const result = await event.promise;
      if (result?.detail?.didError) {
        this.#show();
        return;
      }
      this.#syncCard();
    } catch {
      this.#show();
    }
  };

  #syncCard() {
    const value = this.#inputs()[this.#index]?.value;
    const cardInput = [...(this.#card?.querySelectorAll('swatches-variant-picker-component input[type="radio"]') ?? [])].find(
      (input) => input instanceof HTMLInputElement && input.value === value
    );
    if (!(cardInput instanceof HTMLInputElement) || cardInput.checked) return;
    cardInput.checked = true;
    cardInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #handleBackdrop = (event) => {
    if (event.target === this.refs.dialog && isClickedOutside(event, this.refs.dialog)) this.close();
  };

  #handleClosed = () => {
    unlockScroll(this);
    const target = this.#trigger?.isConnected ? this.#trigger : this.#card?.querySelector('a[href]');
    if (target instanceof HTMLElement) target.focus();
  };

  #handleBreakpoint = () => {
    if (mediaQueryLarge.matches) this.close();
    this.#markTrigger();
  };

  #handleCardSelect = async (event) => {
    try {
      await event.promise;
    } catch {
      return;
    }
    this.#markTrigger();
  };

  #stopCardNavigation = (event) => {
    event.stopPropagation();
  };

  #markTrigger() {
    const more = this.#card?.querySelector('.hidden-swatches__count');
    if (!more) return;
    if (isMobileBreakpoint()) {
      more.setAttribute('aria-haspopup', 'dialog');
    } else {
      more.removeAttribute('aria-haspopup');
    }
  }
}

if (!customElements.get('eme-shade-sheet')) customElements.define('eme-shade-sheet', EmeShadeSheet);
